import {useMemo, useState, useEffect, useRef} from "preact/compat";
import {MapFormatter} from "../../../utils";

// const {MOCK} = require("./useCategoriesFilter.mock.js");

export const useCategoriesFilter = props => {
    // Destructured props
    const {
        availableCategories,
        matches,
        handleCatalogCategories = undefined
    } = props;

    // This state might look duplicated,
    // but it's on purpose in order for the component to work completely standalone
    // without needing any external behavior.
    const [selectedCategories, setSelectedCategories] = useState(new Map());
    // In the same way a flag like this must exist at the component in which this one is being called into,
    // it must exist here as well.
    // It is used for flagging when the operation is ready to be dispatched after the user performed any
    // change to the selectedCategories state.
    const [shouldFilter, setShouldFilter] = useState(false);
    /**
     *  ---> ULTRA HACK 3000 right here, folks! <---
     *
     * This state was supposed to be located at the categoriesFormTree component controller, not here.
     *
     * We had to move it here because that component is being recreated on each breakpoint (desktop and mobile),
     * thus all states were being reset on each reconciliation, and there was no way to persist them.
     *
     * Bad engineering, obviously, but we have no time to fix that issue now.
     * Also, it works, so for now we shall leave it like this.
     * */
    const [collapseController, setCollapseController] = useState([]);

    const currentPath = useRef();

    // Handler for the collapseController state
    const handleCollapseController = props => {
        // Destructured props
        const {data} = props;

        setCollapseController(data);
    }

    // All the data regarding the categories (currently returning the mocked data)
    const data = useMemo(() => {
        // Validates all possible cases not to let the tree be rendered
        // if there aren't any available categories
        if (
            !availableCategories ||
            !Array.isArray(availableCategories) ||
            (
                Array.isArray(availableCategories)
                && availableCategories.length === 0
            )
        ) {
            return [];
        }

        return availableCategories;
    }, [availableCategories]);

    // Memo to control whether the clear filters button should be displayed or not
    const shouldDisplayClearButton = useMemo(() => {
        let hasValue = false;

        // Checks if the categories map have any value in it
        for (let value of selectedCategories.values()) {
            if (value) {
                hasValue = true;
            }
        }

        return hasValue;
    }, [selectedCategories]);

    // Method to handle the selected categories
    const handleCategories = props => {
        // Destructured props
        const {
            action,
            category = undefined
        } = props;

        // Flags that an operation is ready to be made if it wasn't already
        if (!shouldFilter) {
            setShouldFilter(true);
        }

        // Creates a new map to store the selected categories
        const newMap = new Map(selectedCategories);

        switch (action.toUpperCase()) {
            case "CHECK": {
                // This validation is responsible for cleaning
                // any additional category the user may have checked before
                // it checks another "base" category.
                if (category.inputName === "base-category") {
                    newMap.clear();
                }

                newMap.set(category.inputName, category.id);

                // Appends the new category to the state
                setSelectedCategories(newMap);

                break;
            }
            case "UNCHECK": {
                // Deletes the desired category from the map object
                newMap.delete(category.inputName);

                // Appends the new category to the state
                setSelectedCategories(newMap);

                break;
            }
            case "CLEAR": {
                // Clears all categories at once
                setSelectedCategories(new Map());

                if (matches) {
                    syncCategories(true);
                }

                break;
            }
            default:
                return;
        }
    }

    // Handles the input changes
    const handleChange = props => {
        // Destructured props
        const {category} = props;

        // Handles the desired action according to the user input.
        // If the clicked input has already been checked, then it unchecks it.
        handleCategories({
            action:
                selectedCategories.get(category.inputName) !== category.id ?
                    "CHECK" :
                    "UNCHECK",
            category
        })
    }

    // Method to clear all the selected filters when the "Limpar filtros" button is clicked
    const handleClear = () => {
        handleCategories({
            action: "CLEAR"
        })
    }

    // Method to sync the local "selectedCategories" state with the external on, wherever it may be.
    // It already returns the formatted values in an array.
    // There's although, a situation where we have to call it manually, and it is
    // when the user clicks on the "Filtrar" button on mobile devices,
    // as it should filter automatically.
    const syncCategories = (
        empty = undefined // Flag for when user clicks at the "Limpar filtros" on mobile devices
    ) => {
        if (typeof handleCatalogCategories !== "function") {
            return;
        }

        handleCatalogCategories({
            values: !empty ? MapFormatter(selectedCategories) : [],
            shouldFilter
        });
    }

    // This is a method for manually calling both the syncCategories method
    // and the sidebar close event
    const dispatchSyncEvent = () => {
        syncCategories();

        // Here's where it closes the sidebar
        try {
            document.querySelector("#mobile-sidebar-close-button").click();
        } catch (err) {
            console.warn("Error while closing sidebar.")
        }
    }

    // Effect to trigger the state sync event
    useEffect(() => {
        // Only automatically sync if it's not on mobile devices
        if (!matches) {
            syncCategories();
        }

        // Checks if the path has been updated and if any category is selected
        if (currentPath.current !== window.location.pathname && selectedCategories.size) {
            // Clears all the selected categories on page change
            handleCategories({
                action: "CLEAR"
            });
        }
    }, [
        matches,
        selectedCategories,
        window.location.pathname
    ]);

    // Makes the first attribution to the currentPath ref with the current page path
    useEffect(() => {
        currentPath.current = window.location.pathname;
    }, [window.location.pathname])

    return {
        data,
        selectedCategories,
        shouldFilter,
        shouldDisplayClearButton,
        collapseController,
        handleCollapseController,
        handleCategories,
        handleChange,
        handleClear,
        dispatchSyncEvent
    }
}
